import { isJoyOwnedPath } from '@withjoy/site-editing-protocol/routing';

// Which sub-paths of withjoy.com/:handle are Joy's own guest flows rather than a carbon site's pages:
// the server never proxies them to carbon, and the guest site renders them for a Website V2 event.
// The list is `isJoyOwnedPath` from `@withjoy/site-editing-protocol`, shared with the service, plus
// the routes it doesn't carry yet. Import the `/routing` subpath, not the package root: this runs on
// every guest page, and the CJS root re-exports the whole protocol (~41 KB minified vs ~2.4 KB).

// Joy guest routes the shared list doesn't carry yet (first path segment, owning everything under
// it). Remove an entry once the protocol package lists it — until then carbon would 404 the path
// and the guest would land on the site root.
const JOY_OWNED_GUEST_SEGMENTS_PENDING_UPSTREAM: ReadonlySet<string> = new Set([
  // The instant room block booking link, /:handle/instant-room-block/:id (#18324).
  'instant-room-block',
  // The emailed bulk photo zip link, /:handle/photo-download?file=… (PRNT-5077). Redirecting it to the
  // site root would drop its signed `file` query.
  'photo-download',
  // The wedding pass landing link, /:handle/wallet/:passId?guestKey=… (MSG-489). Redirecting it to the
  // site root would drop its signed `guestKey` query.
  'wallet',
  //
  // `map` (the guest Wedding Map, /:handle/map) is deliberately NOT here, and adding it would be a
  // regression rather than a fix. It is gated on `guestWeddingMapRollout`, an Amplitude remote-config
  // flag — not an event flag, so it does not reach `parsedFeatureFlagByEventNameResults.featureFlags`
  // and this decision cannot see it. Claiming the segment would therefore route EVERY WebsiteV2
  // event's /map past the carbon proxy and into charm, where a guest outside the rollout would be
  // served the V1 content the couple left behind — exactly the leak the proxy branch in server.ts
  // exists to prevent. Until `map` is reserved upstream in `@withjoy/site-editing-protocol` (so the
  // service refuses it as a page slug and the rollout is universal), the Wedding Map is reachable on
  // V1 guest sites only.
]);

/** Whether joy-web, not the carbon site, answers `/:handle/<path>` (`path` is what follows the handle). */
export const isJoyOwnedGuestPath = (path: string): boolean => {
  const firstSegment = path.split('/').filter(Boolean)[0]?.toLowerCase();
  return isJoyOwnedPath(path) || (firstSegment !== undefined && JOY_OWNED_GUEST_SEGMENTS_PENDING_UPSTREAM.has(firstSegment));
};
